////////////////////////////////////////////
// DEEPLINK CRYPTO — X25519 SESSION ENCRYPTION (PHANTOM/SOLFLARE PROTOCOL)
////////////////////////////////////////////
//
// BOTH WALLETS SPEAK THE SAME SCHEME: THE DAPP SENDS AN X25519 PUBLIC KEY ON
// connect, THE WALLET ANSWERS WITH ITS OWN, AND EVERY PAYLOAD AFTER THAT IS A
// nacl.box (XSALSA20-POLY1305) UNDER THE DIFFIE-HELLMAN SHARED SECRET WITH A
// FRESH 24-BYTE NONCE. EVERYTHING ON THE WIRE IS BASE58.
import nacl from "tweetnacl";
import bs58 from "bs58";

export interface DlKeypair {
  publicKey: string;
  secretKey: string;
}

// FRESH X25519 KEYPAIR — ONE PER connect ATTEMPT (THE PROTOCOL'S RECOMMENDATION)
export function makeKeypair(): DlKeypair {

  const kp = nacl.box.keyPair();
  return { publicKey: bs58.encode(kp.publicKey), secretKey: bs58.encode(kp.secretKey) };
}

// SHARED SECRET — DH OF THE WALLET'S PUBLIC KEY AND OUR SECRET KEY
export function deriveShared(walletPubB58: string, dappSecretB58: string): string {

  return bs58.encode(nacl.box.before(bs58.decode(walletPubB58), bs58.decode(dappSecretB58)));
}

// ENCRYPT A REQUEST PAYLOAD — JSON -> nacl.box -> BASE58 (+ THE NONCE USED)
export function encryptPayload(payload: unknown, sharedB58: string): { nonce: string; data: string } {

  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const boxed = nacl.box.after(new TextEncoder().encode(JSON.stringify(payload)), nonce, bs58.decode(sharedB58));
  return { nonce: bs58.encode(nonce), data: bs58.encode(boxed) };
}

// DECRYPT A RESPONSE PAYLOAD — BASE58 -> nacl.box.open -> JSON
export function decryptPayload<T>(dataB58: string, nonceB58: string, sharedB58: string): T {

  const opened = nacl.box.open.after(bs58.decode(dataB58), bs58.decode(nonceB58), bs58.decode(sharedB58));
  if (!opened)
    throw new Error("could not decrypt the wallet's response");
  return JSON.parse(new TextDecoder().decode(opened)) as T;
}

// UTF-8 TEXT -> BASE58 — THE signMessage PAYLOAD ENCODING
export function utf8ToB58(text: string): string {

  return bs58.encode(new TextEncoder().encode(text));
}

// BASE58 <-> BASE64 BRIDGES — THE WALLET PROTOCOL IS BASE58, THE FUZE SERVER
// (tx submit / auth verify) SPEAKS BASE64
export function b58ToB64(b58: string): string {

  return btoa(String.fromCharCode(...bs58.decode(b58)));
}

export function b64ToB58(b64: string): string {

  return bs58.encode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
}
